<template>
  <AppContainer>
    <div class="flex items-center justify-between mb-6">
      <PlanTableSearch
        :categories="planStore.selectedFramework?.categories || []"
        @search="searchParams = $event; filterNodes()"
      />

      <AppButton
        type="button"
        class="btn-solid-secondary"
        @click="openFormModal"
      >
        <FontAwesomeIcon :icon="faPlus" />
        Add
      </AppButton>
    </div>

    <AppTable>
      <AppTableHeader>
        <th class="rounded-tl-md">
          {{ `${nodeType[0].toUpperCase()}${nodeType.slice(1)}` }} Name
        </th>

        <th class="rounded-tr-md w-28">
          <span class="sr-only">Manage</span>
        </th>
      </AppTableHeader>

      <AppTableBody
        :loading="false"
        :no-results="nodes.length === 0"
        :colspan="nodes.length ? 3 : 2"
      >
        <tr
          v-for="node in nodes"
          :key="node.id"
        >
          <!-- Name -->
          <td>
            <a
              class="*:!mb-0 *:h-[46px] w-full cursor-pointer"
              @click="openFormModal(node)"
            >
              {{ node.label }}
            </a>
          </td>

          <!-- Manage -->
          <td>
            <AppDropdown>
              <AppDropdownItem
                text="Edit"
                @click="openFormModal(node)"
              />

              <AppDropdownItem
                text="Delete"
                @click="nodeToDelete = node"
              />
            </AppDropdown>
          </td>
        </tr>
      </AppTableBody>
    </AppTable>

    <AppPagination
      v-model:itemsPerPage="paginationData.itemsPerPage"
      v-model:currentPage="paginationData.page"
      :items-per-page="paginationData?.itemsPerPage"
      :current-page="paginationData?.page"
      :total-items="paginationData.total"
      @page-change="paginationData.page = $event"
      @items-per-page-change="paginationData.itemsPerPage = $event"
    />
  </AppContainer>

  <!-- Node Modal -->
  <AppModal
    v-if="showFormModal"
    :title="`${currentNode?.id ? 'Edit ' : 'Add a New '} Project`"
    class="[&_.modal-body]:!p-0"
    disable-backdrop-click
    @close="handleCloseModal"
  >
    <template #body>
      <ProjectForm
        :frameworks="frameworks"
        :node="currentNode"
        @submit="handleUpsertNode"
        @cancel="handleCloseModal"
        @delete="deleteNode(currentNode?.id)"
      />
    </template>
  </AppModal>

  <DeleteNodeModal
    v-if="nodeToDelete"
    :node-name="nodeToDelete.label"
    type="Outcome"
    @confirm="deleteNode(nodeToDelete.id); nodeToDelete = null"
    @close="nodeToDelete = null"
  />
</template>

<script setup>
import { ref, watch } from 'vue';

import { AppContainer, AppPagination } from '@/components/app/index.js';
import { AppDropdown, AppDropdownItem } from '@/components/dropdowns/index.js';
import { PlanTableSearch } from '@/components/forms/search/index.js';
import { AppModal, DeleteNodeModal } from '@/components/modals/index.js';
import { ProjectForm } from '@/components/forms/nodes/index.js';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables/index.js';

import { createCustomShapeRenderer, useNumber } from '@/composables/index.js';
import { MindmapActionMode, NodeType } from '@/const.js';
import { usePlanStore } from '@/store/plan.js';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { AppButton } from '@/components/buttons';

defineProps({
  plan: {
    type: Object,
    default: null,
  },
  frameworks: {
    type: Array,
    default: () => [],
  },
});

const planStore = usePlanStore();

const showFormModal = ref(false);
const currentNode = ref();

const searchParams = ref({ page: 1 });

const paginationData = ref({
  page: 1,
  itemsPerPage: 10,
  total: planStore.nodes.length,
});

const nodeType = ref(NodeType.Project);

const nodes = ref();

const dataSchema = {
  id: null,
  label: null,
  type: NodeType.Project,
  data: {
    level: null,
    writeUp: null,
    stakeholders: null,
    projectDetails: {
      owners: null,
      startDate: null,
      endDate: null,
      projectSector: null,
      budget: 0,
      location: {
        longitude: null,
        latitude: null,
      },
    },
    alignments: [],
    developmentImpact: {
      householdNumber: 0,
      populationNumber: 0,
      wypdNumber: 0,
      temporaryJobNumber: 0,
      permanentJobNumber: 0,
      beneficiarySize: 0,
    },
  },
};

const nodeToDelete = ref(null);

const mode = ref();
const { randomNumber } = useNumber();

const deleteNode = id => {
  planStore.nodes.remove(id);

  filterNodes();
};

const handleCloseModal = () => {
  showFormModal.value = false;
  currentNode.value = null;
  mode.value = null;
};

const openFormModal = (node) => {
  showFormModal.value = true;
  mode.value = null;

  currentNode.value = node ? JSON.parse(JSON.stringify(node)) : JSON.parse(JSON.stringify(dataSchema));
};

const filterNodes = () => {
  let filteredNodes = planStore.nodes.get({ filter: node => node.type === nodeType.value });

  // Filter by search query
  if (searchParams.value.search) {
    filteredNodes = filteredNodes.filter(node => node.label.toLowerCase().includes(searchParams.value.search.toLowerCase()));
  }

  // Filter by framework
  if (planStore.selectedFramework) {
    filteredNodes = filteredNodes.filter(node => {
      const alignment = node.data.alignments.find(alignment => alignment.framework === planStore.selectedFramework.id);

      return alignment?.framework === planStore.selectedFramework.id && alignment.categories.length > 0;
    });
  }

  // Filter by category
  if (searchParams.value.category) {
    filteredNodes = filteredNodes.filter(node => {
      const nodeCategoryFromSelectedFramework = node.data.alignments.find(frameworkCategory => frameworkCategory.framework === planStore.selectedFramework?.id);

      // Check if the node has a category for the selected framework (if any)
      if (!nodeCategoryFromSelectedFramework) {
        return false;
      }

      return nodeCategoryFromSelectedFramework.categories.includes(searchParams.value.category);
    });
  }

  paginationData.value.total = filteredNodes.length;

  // Do pagination
  const start = (paginationData.value.page - 1) * paginationData.value.itemsPerPage;
  const end = start + paginationData.value.itemsPerPage;

  nodes.value = filteredNodes.slice(start, end);
};

const handleUpsertNode = node => {
  if (!node.id) {
    node.id = crypto.randomUUID();
    planStore.nodes.add(node);
  } else {
    planStore.nodes.update(node);
  }
  console.log(node);
  refreshNode(node);
  handleCloseModal();

  filterNodes();
};

const refreshNode = node => {
  const frameworkCategoryItem = planStore.selectedFramework ? node.data.alignments.find(item => item.framework === planStore.selectedFramework.id) : null;
  const category = frameworkCategoryItem ? planStore.selectedFramework.categories.find(category => category.id === frameworkCategoryItem?.category) : null;

  node.color = category ? category.backgroundColor : '#828F9D';

  if (node.type === NodeType.Project) {
    node.ctxRenderer = createCustomShapeRenderer(category?.backgroundColor || '#828F9D');
  }

  planStore.nodes.update(node);
};

const handleAddOutcomeClick = () => {
  currentNode.value = {
    type: NodeType.Outcome,
    size: 32,
    label: null,
    shape: 'circularImage',
    shapeProperties: {
      useBorderWithImage: false,
    },
    imagePadding: 6,
    /*
     * Make the X and Y coordinates a random number in the range [-100, 100) (arbitrary, but on the canvas).
     * This is so that nodes don't overlap when added multiple times without being dragged.
     */
    x: randomNumber(-100, 100),
    y: randomNumber(-100, 100),
    data: dataSchema,
  };
};

const handleAddProjectClick = () => {
  currentNode.value = {
    type: NodeType.Project,
    label: null,
    shape: 'custom',
    /*
     * Make the X and Y coordinates a random number in the range [-100, 100) (arbitrary, but on the canvas).
     * This is so that nodes don't overlap when added multiple times without being dragged.
     */
    x: randomNumber(-100, 100),
    y: randomNumber(-100, 100),
    data: dataSchema,
  };
};

watch(searchParams, () => {
  paginationData.value.page = 1;

  filterNodes();
}, { immediate: true });

watch(paginationData, () => {
  filterNodes();
}, { deep: true });

watch(nodeType, () => {
  paginationData.value.page = 1;

  filterNodes();
});

// Ensures that the nodes are filtered when a framework is selected in the dropdown
watch(() => planStore.selectedFramework, () => {
  paginationData.value.page = 1;

  filterNodes();
});

watch(mode, () => {
  switch (mode.value) {
    case MindmapActionMode.AddProject:
      handleAddProjectClick();

      break;
  }
});

</script>
